import api from './api';
import { umDesdeDetalleTransaccion } from '../utils/umVentaDocumento';

export interface PickingPedidoPendiente {
  nroFormulario: number;
  fechaEmision?: string | null;
  letra?: string | null;
  nroSuc: number;
  nroComprobante?: number | null;
  nroCliente?: number | null;
  nombre?: string | null;
  importeTotal?: number | null;
  pickingIniciado: boolean;
}

export interface PickingItem {
  nroItem: number;
  codigoArticulo?: string | null;
  descripcion?: string | null;
  codBarra?: string | null;
  cantidadPedida: number;
  cantidadPickeada: number;
  cantidadPendiente: number;
  um?: string | null;
}

export interface PickingPedidoDetalle {
  nroFormulario: number;
  fechaEmision?: string | null;
  letra?: string | null;
  nroSuc: number;
  nroComprobante?: number | null;
  nroCliente?: number | null;
  nombre?: string | null;
  importeTotal?: number | null;
  asentado?: string | null;
  modoCodigoBarra: boolean;
  totalPedido: number;
  totalPickeado: number;
  totalPendiente: number;
  completo: boolean;
  items?: PickingItem[] | null;
}

export interface PickingLecturaResult {
  codigoArticulo?: string | null;
  descripcion?: string | null;
  cantidadAplicada: number;
  cantidadPedida: number;
  cantidadPickeada: number;
  cantidadPendiente: number;
  /** false = la lectura no se aplicó (alerta de cantidad/artículo). */
  lecturaAplicada?: boolean;
  bloquearLector?: boolean;
  requiereAceptacionManual?: boolean;
  /** Opcional: algunos entornos lo envían aunque no figure en Swagger. */
  codigo?: string | null;
  message?: string | null;
  mensaje?: string | null;
  pedido: PickingPedidoDetalle;
}

export interface PickingEvento {
  id: number;
  fechaHora: string;
  codigoArticulo?: string | null;
  descripcion?: string | null;
  cantidad?: number | null;
  usuario?: string | null;
  evento?: string | null;
}

/** Pedido generado con lo pendiente (Swagger: PickingPedidoFaltantesDto). */
export interface PickingPedidoFaltantes {
  nroFormulario: number;
  letra?: string | null;
  nroSuc: number;
  nroComprobante?: number | null;
  importeTotal?: number | null;
  items?: PickingItem[] | null;
}

/** @deprecated Usar PickingPedidoFaltantes */
export type PickingPedidoEspera = PickingPedidoFaltantes;

/** Acciones al cerrar con pendientes (valores alineados a la tarea / API). */
export type PickingAccionPendiente = 'Cancelar' | 'EnEspera';

export interface PickingListoRequest {
  confirmarParcial?: boolean;
  accionPendiente?: PickingAccionPendiente | null;
}

export interface PickingListoResult {
  nroFormulario: number;
  asentado?: string | null;
  cerrado?: boolean;
  codigo?: string | null;
  message?: string | null;
  mensaje?: string | null;
  bloquearLector?: boolean;
  requiereAceptacionManual?: boolean;
  parcial: boolean;
  pedidoFaltantes?: PickingPedidoFaltantes | null;
  /** Compat con respuestas anteriores. */
  pedidoEnEspera?: PickingPedidoFaltantes | null;
}

export interface PickingPendientesSearch {
  nroCliente?: number;
  busqueda?: string;
}

type PickingListoApiPayload = PickingListoResult & {
  PedidoFaltantes?: PickingPedidoFaltantes | null;
  PedidoEnEspera?: PickingPedidoFaltantes | null;
  pedido_faltantes?: PickingPedidoFaltantes | null;
};

function normalizePickingItem(raw: PickingItem): PickingItem {
  return {
    ...raw,
    um: umDesdeDetalleTransaccion(raw) || raw.um || undefined,
  };
}

function normalizePickingDetalle(raw: PickingPedidoDetalle | null | undefined): PickingPedidoDetalle {
  const data = raw ?? ({} as PickingPedidoDetalle);
  return {
    ...data,
    items: (data.items ?? []).map(normalizePickingItem),
  };
}

function normalizeListoResult(raw: PickingListoApiPayload | null | undefined): PickingListoResult {
  const data = raw ?? ({} as PickingListoApiPayload);
  const faltantesRaw =
    data.pedidoFaltantes ??
    data.PedidoFaltantes ??
    data.pedidoEnEspera ??
    data.PedidoEnEspera ??
    data.pedido_faltantes ??
    null;
  const faltantes = faltantesRaw
    ? {
        ...faltantesRaw,
        items: (faltantesRaw.items ?? []).map(normalizePickingItem),
      }
    : null;

  return {
    ...data,
    cerrado: data.cerrado ?? (faltantes ? true : undefined),
    parcial: Boolean(data.parcial),
    pedidoFaltantes: faltantes,
    pedidoEnEspera: faltantes,
  };
}

const pickingVentaService = {
  getPendientes: async (params: PickingPendientesSearch = {}): Promise<PickingPedidoPendiente[]> => {
    const response = await api.get<PickingPedidoPendiente[]>('/api/PickingVentas/pendientes', {
      params: {
        nroCliente: params.nroCliente && params.nroCliente > 0 ? params.nroCliente : undefined,
        busqueda: params.busqueda?.trim() || undefined,
      },
    });
    return response.data ?? [];
  },

  getDetalle: async (id: number): Promise<PickingPedidoDetalle> => {
    const response = await api.get<PickingPedidoDetalle>(`/api/PickingVentas/${id}`);
    return normalizePickingDetalle(response.data);
  },

  leer: async (id: number, codigo: string, cantidad?: number): Promise<PickingLecturaResult> => {
    const response = await api.post<PickingLecturaResult>(`/api/PickingVentas/${id}/leer`, {
      codigo,
      cantidad: cantidad && cantidad > 0 ? cantidad : null,
    });
    const data = response.data;
    return {
      ...data,
      pedido: normalizePickingDetalle(data.pedido),
    };
  },

  deshacer: async (id: number, codigoArticulo: string, cantidad: number): Promise<PickingPedidoDetalle> => {
    const response = await api.post<PickingPedidoDetalle>(`/api/PickingVentas/${id}/deshacer`, {
      codigoArticulo,
      cantidad,
    });
    return normalizePickingDetalle(response.data);
  },

  getEventos: async (id: number): Promise<PickingEvento[]> => {
    const response = await api.get<PickingEvento[]>(`/api/PickingVentas/${id}/eventos`);
    return response.data ?? [];
  },

  listo: async (id: number, request: PickingListoRequest = {}): Promise<PickingListoResult> => {
    const response = await api.post<PickingListoApiPayload>(`/api/PickingVentas/${id}/listo`, {
      confirmarParcial: Boolean(request.confirmarParcial),
      accionPendiente: request.accionPendiente ?? null,
    });
    return normalizeListoResult(response.data);
  },
};

export default pickingVentaService;
